import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Input,
  Box,
  Portal,
} from '@chakra-ui/react';
import { Icons } from '@texas/components/shared/Icons';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

interface DatePreset {
  value: string | null;
  translationKey: string;
  calculateDate?: () => Date | null;
}

const DATE_PRESETS: DatePreset[] = [
  {
    value: null,
    translationKey: 'supplierCoverageHeatmap.dateFilter.allTime',
  },
  {
    value: 'last30days',
    translationKey: 'supplierCoverageHeatmap.dateFilter.last30days',
    calculateDate: () => {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      return date;
    },
  },
  {
    value: 'last90days',
    translationKey: 'supplierCoverageHeatmap.dateFilter.last90days',
    calculateDate: () => {
      const date = new Date();
      date.setDate(date.getDate() - 90);
      return date;
    },
  },
  {
    value: 'last6months',
    translationKey: 'supplierCoverageHeatmap.dateFilter.last6months',
    calculateDate: () => {
      const date = new Date();
      date.setMonth(date.getMonth() - 6);
      return date;
    },
  },
  {
    value: 'lastyear',
    translationKey: 'supplierCoverageHeatmap.dateFilter.lastYear',
    calculateDate: () => {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 1);
      return date;
    },
  },
  {
    value: 'custom',
    translationKey: 'supplierCoverageHeatmap.dateFilter.custom',
  },
];

export function calculateCreatedAfterDate(preset: string | null): Date | null {
  if (!preset) return null;

  const presetConfig = DATE_PRESETS.find((p) => p.value === preset);
  if (presetConfig?.calculateDate) {
    return presetConfig.calculateDate();
  }

  try {
    return new Date(preset);
  } catch {
    return null;
  }
}

interface Props {
  selectedPreset: string | null;
  onPresetChange: (preset: string | null) => void;
}

export function DateFilterDropdown({ selectedPreset, onPresetChange }: Props) {
  const { t } = useTranslation();
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customDate, setCustomDate] = useState('');

  const getDisplayText = () => {
    if (!selectedPreset) {
      return t('supplierCoverageHeatmap.dateFilter.allTime');
    }

    const preset = DATE_PRESETS.find((p) => p.value === selectedPreset);
    if (preset) {
      return t(preset.translationKey as any);
    }

    try {
      const date = new Date(selectedPreset);
      return date.toLocaleDateString();
    } catch {
      return t('supplierCoverageHeatmap.dateFilter.custom');
    }
  };

  const handleCustomDateSubmit = () => {
    if (customDate) {
      onPresetChange(new Date(customDate).toISOString());
      setShowCustomInput(false);
      setCustomDate('');
    }
  };

  const handlePresetSelect = (preset: DatePreset) => {
    if (preset.value === 'custom') {
      setShowCustomInput(true);
    } else {
      onPresetChange(preset.value);
      setShowCustomInput(false);
    }
  };

  return (
    <Menu closeOnSelect={!showCustomInput} isLazy={true}>
      <MenuButton
        as={Button}
        rightIcon={<Icons.ChevronDown />}
        size="sm"
        variant="ghost"
      >
        {t('supplierCoverageHeatmap.dateFilter.since' as any)}:{' '}
        {getDisplayText()}
      </MenuButton>
      <Portal>
        <MenuList>
          {DATE_PRESETS.map((preset) => (
            <MenuItem
              key={preset.value ?? 'all'}
              onClick={() => handlePresetSelect(preset)}
              closeOnSelect={preset.value !== 'custom'}
            >
              {t(preset.translationKey as any)}
              {preset.value === 'custom' && showCustomInput && (
                <Box
                  mt={2}
                  ml={2}
                  onClick={(e) => e.stopPropagation()}
                  w="full"
                >
                  <Input
                    type="date"
                    size="sm"
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                    onKeyUp={(e) => {
                      if (e.key === 'Enter') handleCustomDateSubmit();
                    }}
                  />
                  <Button size="xs" mt={1} onClick={handleCustomDateSubmit}>
                    {t('general.apply')}
                  </Button>
                </Box>
              )}
            </MenuItem>
          ))}
        </MenuList>
      </Portal>
    </Menu>
  );
}
